import { useMutation, useQueryClient } from '@tanstack/react-query';

import type { ApiError } from '@/api/client.types';

import type {
  AssignmentScore,
  CoachUnitScore,
  GradebookAssignment,
  GradebookStudent,
  StudentGradesState,
} from './gradebookTypes';
import type { SaveCoachScoresParams } from './gradebookService';
import { useKuduGradebookService } from './gradebookService';
import { studentGradesQueryKeyPrefix } from './useStudentGrades';

function emptyCoachScore(assignment: GradebookAssignment): AssignmentScore {
  return {
    coachUnitScores: [],
    componentId: assignment.componentId,
    componentTitle: assignment.title,
    componentUid: assignment.componentUid,
    homeworkPossible: 0,
    homeworkScore: null,
    participationPossible: assignment.participationMaxScore,
    participationScore: null,
    preLecturePossible: 0,
    preLectureQuestionScores: [],
    preLectureScore: null,
    questionScores: [],
    sectionScores: [],
  };
}

/**
 * A coach assignment's score with some units set to new values. The total moves
 * by the change rather than being recomputed from the listed units, because
 * the stored total also counts units the gradebook does not list (not yet open,
 * or without points). markAdjusted marks the set units as instructor-adjusted,
 * as the backend will once they are saved.
 */
export function applyCoachScoresToAssignment(
  assignment: GradebookAssignment,
  assignmentScore: AssignmentScore | null,
  scores: Record<string, number>,
  markAdjusted: boolean,
): AssignmentScore {
  const base = assignmentScore ?? emptyCoachScore(assignment);
  const current = base.coachUnitScores ?? [];
  const units = assignment.coachUnits ?? [];
  let delta = 0;
  const coachUnitScores: CoachUnitScore[] = units.map((unit) => {
    const existing = current.find((score) => score.unitUid === unit.uid);
    if (!(unit.uid in scores)) {
      return existing ?? { adjusted: false, score: null, unitUid: unit.uid };
    }
    delta += scores[unit.uid] - (existing?.score ?? 0);
    return {
      adjusted: markAdjusted || (existing?.adjusted ?? false),
      score: scores[unit.uid],
      unitUid: unit.uid,
    };
  });
  const anyScored = base.participationScore !== null || Object.keys(scores).length > 0;
  return {
    ...base,
    coachUnitScores,
    participationPossible: assignment.participationMaxScore,
    participationScore: anyScored ? (base.participationScore ?? 0) + delta : null,
  };
}

export function applyCoachScoresToStudent(
  student: GradebookStudent,
  assignment: GradebookAssignment,
  scores: Record<string, number>,
): GradebookStudent {
  const nextAssignmentScores = {
    ...student.assignmentScores,
    [assignment.componentUid]: applyCoachScoresToAssignment(
      assignment,
      student.assignmentScores[assignment.componentUid] ?? null,
      scores,
      true,
    ),
  };
  return {
    ...student,
    assignmentScores: nextAssignmentScores,
    hasLoadedGrades: true,
    totalParticipationScore: Object.values(nextAssignmentScores).reduce(
      (total, score) => total + (score.participationScore ?? 0),
      0,
    ),
  };
}

/**
 * Saves an instructor's scores for a coach's units through
 * SetKaiCoachUnitScores, updating the gradebook straight away and rolling back
 * if the save fails.
 */
export function useSaveCoachScores(courseId: string | undefined) {
  const queryClient = useQueryClient();
  const service = useKuduGradebookService();

  return useMutation<
    void,
    ApiError,
    SaveCoachScoresParams,
    { previousStates: Array<[readonly unknown[], StudentGradesState | undefined]> }
  >({
    mutationFn: async (params) => {
      if (!service || !courseId) {
        const error = new Error('Unable to save coach scores right now.') as ApiError;
        error.kind = 'data';
        throw error;
      }
      await service.saveCoachUnitScores(params);
    },
    onMutate: async (params) => {
      if (!courseId) {
        return { previousStates: [] };
      }
      const previousStates = queryClient.getQueriesData<StudentGradesState>({
        queryKey: studentGradesQueryKeyPrefix(courseId),
      });
      queryClient.setQueriesData<StudentGradesState>(
        { queryKey: studentGradesQueryKeyPrefix(courseId) },
        (currentState) =>
          currentState
            ? {
                ...currentState,
                students: currentState.students.map((student) =>
                  student.id === params.student.id
                    ? applyCoachScoresToStudent(student, params.assignment, params.scores)
                    : student,
                ),
              }
            : currentState,
      );
      return { previousStates };
    },
    onError: (_error, _variables, context) => {
      context?.previousStates.forEach(([queryKey, data]) => {
        queryClient.setQueryData(queryKey, data);
      });
    },
  });
}
